import { usePlaygroundLocalMsg } from "@faire/prototype-playground/platform/localization";
import { Add } from "@faire/slate-icons/Add";
import { Check } from "@faire/slate-icons/Check";
import { ChevronDown } from "@faire/slate-icons/ChevronDown";
import { ChevronUp } from "@faire/slate-icons/ChevronUp";
import { Button } from "@faire/slate/components/button";
import { TextInput } from "@faire/slate/components/text-input";
import { tsv } from "@faire/slate/lib/tsv";
import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";

import type { FavoriteCollection } from "./FavoriteCollectionPicker";
import {
  getNavigationHeaderBottom,
  getServerNavigationHeaderBottom,
  subscribeToNavigationHeaderPosition,
} from "./navigationHeaderPosition";
import { ALL_FAVORITES_COLLECTION_ID } from "./savedStore";

// Gap kept below the global nav for top-right toasts so they never overlap it.
const TOP_RIGHT_NAV_OFFSET_PX = 16;
// "Close on timer": idle delay before the toast dismisses itself. The collapsed
// toast closes if untouched; once organizing, every add/remove restarts it.
const CLOSE_ON_TIMER_IDLE_MS = 2000;

type ToastTheme = "light" | "dark";
type ToastPlacement = "bottom-center" | "top-right";
type ToastCollapsedLayout = "inline" | "stacked";

interface FavoriteCollectionToastProps {
  collections: FavoriteCollection[];
  /** Collapsed layout: "inline" puts "Saved" and the link on one row;
   * "stacked" stacks the link under "Saved" as a subtitle. */
  collapsedLayout?: ToastCollapsedLayout;
  /** "Close on timer": auto-dismiss the untouched collapsed toast after a short
   * idle delay, and — once organizing — close a couple of seconds after the
   * last add/remove (each action restarts the timer). */
  closeOnTimer?: boolean;
  /** Stage collection add/remove locally and commit only when the retailer
   * presses "Save" (shown in place of "New collection" once anything has
   * changed). Dismissing without saving discards the staged changes. */
  confirmWithSave?: boolean;
  /** Open already expanded (e.g. re-saving an already-saved product to organize
   * or remove it) instead of collapsed. */
  initialExpanded?: boolean;
  placement?: ToastPlacement;
  productImageUrl: string;
  /** When set, the collapsed toast reads "Saved to {destination}" and the
   * expanded drawer lists every collection (including the all-favorites
   * destination) inline instead of showing a "Saved / Remove" header — the
   * Figma bottom-aligned dark treatment. */
  savedDestinationName?: string;
  selectedCollectionIds: string[];
  theme?: ToastTheme;
  /** Theme to switch to once expanded. When set (e.g. dark collapsed pill that
   * opens into a light organize drawer), the collapsed toast uses `theme` and
   * the expanded drawer uses `expandedTheme`. */
  expandedTheme?: ToastTheme;
  onClose: () => void;
  onCreateCollection: (collectionName: string) => void;
  onToggleCollection: (collectionId: string) => void;
}

/**
 * Post-save toast: collapsed it confirms the save and offers an "Add to a
 * collection" link; tapping it expands the drawer into the collections list.
 * Themeable (light / dark) and placeable (bottom-center / top-right). Dismisses
 * on outside-click or on scroll (no explicit close button).
 */
export const FavoriteCollectionToast: React.FC<
  FavoriteCollectionToastProps
> = ({
  collections,
  collapsedLayout = "inline",
  closeOnTimer = false,
  confirmWithSave = false,
  initialExpanded,
  placement = "bottom-center",
  productImageUrl,
  savedDestinationName,
  selectedCollectionIds,
  theme = "light",
  expandedTheme,
  onClose,
  onCreateCollection,
  onToggleCollection,
}) => {
  const shouldReduceMotion = useReducedMotion();
  const navigationHeaderBottom = useSyncExternalStore(
    subscribeToNavigationHeaderPosition,
    getNavigationHeaderBottom,
    getServerNavigationHeaderBottom
  );
  const toastRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(initialExpanded ?? false);
  const [isCreating, setIsCreating] = useState(false);
  const [collectionName, setCollectionName] = useState("");
  const [hasInteracted, setHasInteracted] = useState(false);
  const actionTimeoutRef = useRef<number | null>(null);

  // Close on timer: the untouched collapsed toast dismisses after the idle
  // delay; any interaction with it (hover, tap, focus) cancels that.
  useEffect(() => {
    if (!closeOnTimer || isExpanded || hasInteracted) {
      return undefined;
    }
    const timeoutId = window.setTimeout(onClose, CLOSE_ON_TIMER_IDLE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [closeOnTimer, hasInteracted, isExpanded, onClose]);

  const clearActionTimer = () => {
    if (actionTimeoutRef.current === null) {
      return;
    }
    window.clearTimeout(actionTimeoutRef.current);
    actionTimeoutRef.current = null;
  };
  // Close on timer: each add/remove restarts the countdown, so a retailer can
  // move across several collections before the toast dismisses.
  const restartActionTimer = () => {
    if (!closeOnTimer) {
      return;
    }
    clearActionTimer();
    actionTimeoutRef.current = window.setTimeout(() => {
      actionTimeoutRef.current = null;
      onClose();
    }, CLOSE_ON_TIMER_IDLE_MS);
  };
  useEffect(() => clearActionTimer, []);

  const savedLabel = usePlaygroundLocalMsg("Saved");
  const savedToLabel = usePlaygroundLocalMsg("Saved to");
  const addToCollectionLabel = usePlaygroundLocalMsg("Add to a collection");
  const removeLabel = usePlaygroundLocalMsg("Remove");
  const newCollectionLabel = usePlaygroundLocalMsg("New collection");
  const createLabel = usePlaygroundLocalMsg("Create");
  const saveLabel = usePlaygroundLocalMsg("Save");

  // Dismiss on outside-click or on scroll (anywhere outside the toast).
  useEffect(() => {
    const isInsideToast = (target: EventTarget | null) =>
      toastRef.current?.contains(target as Node) ?? false;
    const handlePointerDown = (event: MouseEvent) => {
      if (!isInsideToast(event.target)) {
        onClose();
      }
    };
    const handleScroll = (event: Event) => {
      if (!isInsideToast(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [onClose]);

  const organizableCollections = collections.filter(
    (collection) => collection.id !== ALL_FAVORITES_COLLECTION_ID
  );
  // Figma bottom-aligned dark treatment: no "Saved / Remove" header, and the
  // all-favorites destination shows up as a (checked) row in the list.
  const showSavedDestination = savedDestinationName != null;
  const listedCollections = showSavedDestination
    ? collections
    : organizableCollections;
  // Bottom-aligned dark treatment: the collapsed toast hugs its text and grows
  // to the full expanded width on tap (framer `layout` tweens the width). Capped
  // at the expanded width so the collapsed pill is never wider than expanded.
  const getToastWidth = (): "full" | "hug" | "max" => {
    if (!showSavedDestination) {
      return "full";
    }
    return isExpanded ? "max" : "hug";
  };
  // The collapsed pill uses `theme`; once expanded it can switch to
  // `expandedTheme` (e.g. dark pill → light organize drawer).
  const activeTheme = isExpanded ? (expandedTheme ?? theme) : theme;

  const handleRemove = () => {
    onToggleCollection(ALL_FAVORITES_COLLECTION_ID);
    onClose();
  };

  // Confirm-with-save: toggles are staged here and only committed when the
  // retailer presses "Save". Removing from all-favorites stays immediate.
  const committedOrganizableIds = selectedCollectionIds.filter(
    (collectionId) => collectionId !== ALL_FAVORITES_COLLECTION_ID
  );
  const [pendingSelectedIds, setPendingSelectedIds] = useState<string[]>(
    committedOrganizableIds
  );
  const effectiveSelectedIds = confirmWithSave
    ? [
        ...pendingSelectedIds,
        ...selectedCollectionIds.filter(
          (collectionId) => collectionId === ALL_FAVORITES_COLLECTION_ID
        ),
      ]
    : selectedCollectionIds;
  const hasPendingChanges =
    confirmWithSave &&
    (pendingSelectedIds.length !== committedOrganizableIds.length ||
      pendingSelectedIds.some(
        (collectionId) => !committedOrganizableIds.includes(collectionId)
      ));
  const handleRowToggle = (collectionId: string) => {
    if (!confirmWithSave || collectionId === ALL_FAVORITES_COLLECTION_ID) {
      onToggleCollection(collectionId);
      restartActionTimer();
      return;
    }
    setPendingSelectedIds((currentPendingSelectedIds) =>
      currentPendingSelectedIds.includes(collectionId)
        ? currentPendingSelectedIds.filter(
            (pendingId) => pendingId !== collectionId
          )
        : [...currentPendingSelectedIds, collectionId]
    );
  };
  const handleSave = () => {
    const changedCollectionIds = [
      ...pendingSelectedIds.filter(
        (collectionId) => !committedOrganizableIds.includes(collectionId)
      ),
      ...committedOrganizableIds.filter(
        (collectionId) => !pendingSelectedIds.includes(collectionId)
      ),
    ];
    changedCollectionIds.forEach(onToggleCollection);
    onClose();
  };

  const handleSubmitNewCollection = () => {
    const trimmed = collectionName.trim();
    if (!trimmed) {
      return;
    }
    onCreateCollection(trimmed);
  };

  const renderFooterContent = () => {
    // Save is the same 40px height as the "New collection" row below, so the
    // footer (and the toast's bottom edge) doesn't shift between the two states.
    if (hasPendingChanges) {
      return (
        <Button fullWidth onClick={handleSave} size="small" type="button">
          {saveLabel}
        </Button>
      );
    }
    if (isCreating) {
      return (
        <form
          className={createFormStyle.cls()}
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmitNewCollection();
          }}
        >
          <TextInput
            autoFocus
            label={newCollectionLabel}
            onChange={(event) => setCollectionName(event.target.value)}
            placeholder="Holiday cards 2027"
            value={collectionName}
          />
          <Button size="small" type="submit">
            {createLabel}
          </Button>
        </form>
      );
    }
    return (
      <button
        className={newCollectionRowStyle.cls({
          withThumbnail: confirmWithSave,
        })}
        onClick={() => {
          clearActionTimer();
          setIsCreating(true);
        }}
        type="button"
      >
        {confirmWithSave ? (
          <NewCollectionPlaceholder theme={activeTheme} />
        ) : null}
        <p className="type-fs-paragraph-medium min-w-0 flex-1 truncate">
          {newCollectionLabel}
        </p>
        <span
          className={selectionIndicatorStyle.cls({
            state: `${activeTheme}-unselected`,
          })}
        >
          <Add height="16px" width="16px" />
        </span>
      </button>
    );
  };

  const entryOffset = placement === "top-right" ? -16 : 16;
  // Top-right toasts anchor just below the global nav so they never overlap it.
  const positionerTop =
    placement === "top-right"
      ? navigationHeaderBottom + TOP_RIGHT_NAV_OFFSET_PX
      : undefined;
  // Elevation tokens (--slate-elevation-*) don't emit a working Tailwind
  // shadow/drop-shadow utility in this build, so apply the literal value inline
  // (same approach as FavoriteCollectionPicker's popover).
  const boxShadow =
    activeTheme === "dark"
      ? "0 2px 24px 0 rgba(0, 0, 0, 0.2)"
      : "0 2px 12px 0 rgba(0, 0, 0, 0.15)";
  // Chevron direction follows the Figma per theme (dark points up, light down).
  const CollapsedChevron = theme === "dark" ? ChevronUp : ChevronDown;
  const chevronColor =
    theme === "dark" ? "var(--slate-color-icon-primary-inverse)" : undefined;

  return createPortal(
    <div
      className={positionerStyle.cls({ placement })}
      style={positionerTop == null ? undefined : { top: positionerTop }}
    >
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className={toastStyle.cls({
          theme: activeTheme,
          width: getToastWidth(),
        })}
        exit={{ opacity: 0, y: entryOffset }}
        initial={shouldReduceMotion ? false : { opacity: 0, y: entryOffset }}
        layout={!shouldReduceMotion}
        onFocusCapture={() => setHasInteracted(true)}
        onPointerDown={() => setHasInteracted(true)}
        onPointerMove={() => setHasInteracted(true)}
        ref={toastRef}
        role="status"
        style={{ boxShadow }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      >
        {isExpanded && !showSavedDestination ? (
          <div className={savedRowStyle.cls()}>
            <div className={imageFrameStyle.cls()}>
              <img
                alt=""
                className="size-full object-cover"
                src={productImageUrl}
              />
              <ImageScrim />
            </div>
            <p className="type-fs-paragraph-medium min-w-0 flex-1 truncate">
              {savedLabel}
            </p>
            <button
              className={removeButtonStyle.cls()}
              onClick={handleRemove}
              type="button"
            >
              {removeLabel}
            </button>
          </div>
        ) : null}

        {!isExpanded ? (
          <button
            aria-expanded={false}
            className={headerRowStyle.cls({ fit: showSavedDestination })}
            onClick={() => setIsExpanded(true)}
            type="button"
          >
            <div className={imageFrameStyle.cls()}>
              <img
                alt=""
                className="size-full object-cover"
                src={productImageUrl}
              />
              <ImageScrim />
            </div>
            {showSavedDestination ? (
              <>
                <p className="type-fs-paragraph min-w-0 truncate">
                  {savedToLabel}{" "}
                  <span className="type-fs-paragraph-medium">
                    {savedDestinationName}
                  </span>
                </p>
                <span aria-hidden className={collapsedChevronStyle.cls()}>
                  <CollapsedChevron
                    color={chevronColor}
                    height="20px"
                    width="20px"
                  />
                </span>
              </>
            ) : collapsedLayout === "stacked" ? (
              <>
                <div className={collapsedStackStyle.cls()}>
                  <p className="type-fs-paragraph-medium truncate">
                    {savedLabel}
                  </p>
                  <p className="type-fs-paragraph text-fs-text-primary truncate">
                    {addToCollectionLabel}
                  </p>
                </div>
                <span aria-hidden className={collapsedChevronStyle.cls()}>
                  <CollapsedChevron
                    color={chevronColor}
                    height="20px"
                    width="20px"
                  />
                </span>
              </>
            ) : (
              <>
                <p className="type-fs-paragraph-medium shrink-0">
                  {savedLabel}
                </p>
                <span className={collapsedActionStyle.cls()}>
                  <span className="type-fs-paragraph">
                    {addToCollectionLabel}
                  </span>
                  <CollapsedChevron
                    color={chevronColor}
                    height="20px"
                    width="20px"
                  />
                </span>
              </>
            )}
          </button>
        ) : null}

        {isExpanded ? (
          <motion.div
            animate={{ opacity: 1 }}
            initial={shouldReduceMotion ? false : { opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeOut", delay: 0.05 }}
          >
            <div
              className={collectionListStyle.cls({
                withTopPadding: showSavedDestination,
              })}
            >
              {listedCollections.map((collection) => (
                <CollectionRow
                  collection={collection}
                  isSelected={effectiveSelectedIds.includes(collection.id)}
                  key={collection.id}
                  onToggle={() => handleRowToggle(collection.id)}
                  theme={activeTheme}
                />
              ))}
            </div>
            <div className={footerStyle.cls({ theme: activeTheme })}>
              {renderFooterContent()}
            </div>
          </motion.div>
        ) : null}
      </motion.div>
    </div>,
    document.body
  );
};

interface CollectionRowProps {
  collection: FavoriteCollection;
  isSelected: boolean;
  theme: ToastTheme;
  onToggle: () => void;
}

const CollectionRow: React.FC<CollectionRowProps> = ({
  collection,
  isSelected,
  theme,
  onToggle,
}) => (
  <button
    aria-label={collection.name}
    aria-pressed={isSelected}
    className={collectionRowStyle.cls()}
    onClick={onToggle}
    type="button"
  >
    <span className={collectionThumbnailStyle.cls({ theme })}>
      {collection.imageUrl ? (
        <img
          alt=""
          className="size-full object-cover"
          src={collection.imageUrl}
        />
      ) : null}
      <ImageScrim />
    </span>
    <p className="type-fs-paragraph-medium min-w-0 flex-1 truncate">
      {collection.name}
    </p>
    <span
      className={selectionIndicatorStyle.cls({
        state: `${theme}-${isSelected ? "selected" : "unselected"}`,
      })}
    >
      {isSelected ? (
        <Check
          color={
            theme === "light"
              ? "var(--slate-color-icon-primary-inverse)"
              : "var(--slate-color-action-icon-default)"
          }
          height="16px"
          width="16px"
        />
      ) : (
        <Add height="16px" width="16px" />
      )}
    </span>
  </button>
);

// Empty-collection thumbnail (2×2 tile grid), matching the popover's
// "Add to collection" / "New collection" rows.
const NewCollectionPlaceholder: React.FC<{ theme: ToastTheme }> = ({
  theme,
}) => (
  <span aria-hidden className={newCollectionPlaceholderStyle.cls({ theme })}>
    <span className={newCollectionPlaceholderTileStyle.cls({ theme })} />
    <span className={newCollectionPlaceholderTileStyle.cls({ theme })} />
    <span className={newCollectionPlaceholderTileStyle.cls({ theme })} />
    <span className={newCollectionPlaceholderTileStyle.cls({ theme })} />
  </span>
);

const ImageScrim: React.FC = () => (
  <span
    aria-hidden
    className="pointer-events-none absolute inset-0"
    style={{ backgroundColor: "rgba(0, 0, 0, 0.03)" }}
  />
);

const positionerStyle = tsv({
  base: "fixed z-[1100] w-[min(382px,calc(100vw-32px))]",
  variants: {
    placement: {
      "bottom-center":
        "bottom-(--slate-dimensions-6) left-1/2 -translate-x-1/2",
      "top-right": "right-(--slate-dimensions-6)",
    },
  },
});

const toastStyle = tsv({
  base: "rounded-fs-container-default flex flex-col overflow-hidden transition-colors duration-200",
  variants: {
    theme: {
      light: "bg-fs-surface-primary text-fs-text-primary",
      // Override the text-color token so every descendant `type-fs-*` class
      // (which hard-sets color to --slate-color-text-primary) renders white.
      dark: [
        "bg-fs-surface-primary-inverse text-fs-text-primary-inverse",
        "[--slate-color-text-primary:var(--slate-color-text-primary-inverse)]",
      ],
    },
    // Within the fixed-width centered positioner: "full" fills it; "hug" sizes
    // to content and stays centered (collapsed pill, capped at the positioner
    // width via max-w-full); "max" is the full expanded width the pill grows
    // into. `layout` tweens the width between hug and max.
    width: {
      full: "w-full",
      hug: "mx-auto w-max max-w-full",
      max: "mx-auto w-full",
    },
  },
});

const headerRowStyle = tsv({
  base: [
    "focusable flex items-center text-left",
    "gap-(--slate-spacing-element-small)",
  ],
  variants: {
    // Collapsed "Saved to …" pill hugs its content with tighter 12px padding;
    // the other collapsed layouts fill the fixed-width toast.
    fit: {
      true: "w-auto p-(--slate-dimensions-3)",
      false: "w-full p-(--slate-spacing-element-large)",
    },
  },
});

const savedRowStyle = tsv({
  base: [
    "flex w-full items-center gap-(--slate-spacing-element-small)",
    "px-(--slate-spacing-element-large) pt-(--slate-spacing-element-large) pb-(--slate-spacing-group-medium)",
  ],
});

const collapsedActionStyle = tsv({
  base: [
    "ml-auto flex shrink-0 items-center",
    "gap-(--slate-spacing-group-xsmall)",
  ],
});

const collapsedStackStyle = tsv({
  base: "flex min-w-0 flex-1 flex-col",
});

const collapsedChevronStyle = tsv({
  base: "flex size-6 shrink-0 items-center justify-center",
});

const removeButtonStyle = tsv({
  base: "type-fs-paragraph focusable interactive ml-auto shrink-0",
});

const imageFrameStyle = tsv({
  base: "rounded-fs-component-default relative size-10 shrink-0 overflow-hidden",
});

const collectionListStyle = tsv({
  base: [
    "flex flex-col",
    "gap-(--slate-spacing-group-medium) px-(--slate-spacing-element-large) pb-(--slate-spacing-group-medium)",
  ],
  variants: {
    withTopPadding: {
      true: "pt-(--slate-spacing-element-large)",
      false: "",
    },
  },
});

const collectionRowStyle = tsv({
  base: [
    "group focusable interactive rounded-fs-component-default flex min-h-10 w-full items-center text-left",
    "gap-(--slate-spacing-group-medium)",
  ],
});

const collectionThumbnailStyle = tsv({
  base: [
    "bg-fs-surface-tertiary rounded-fs-component-default relative size-10 shrink-0 overflow-hidden border",
  ],
  variants: {
    theme: {
      light: "border-fs-border-muted",
      dark: "border-(--slate-color-grey-100)/10",
    },
  },
});

const selectionIndicatorStyle = tsv({
  base: [
    "flex size-8 shrink-0 items-center justify-center",
    "rounded-fs-component-round transition-colors",
  ],
  variants: {
    state: {
      "light-selected":
        "bg-fs-action-surface-default text-fs-icon-primary-inverse",
      "light-unselected":
        "border-fs-action-border-subdued bg-fs-action-surface-inverse text-fs-text-primary border",
      "dark-selected":
        "bg-fs-action-surface-inverse text-fs-action-icon-default",
      "dark-unselected":
        "bg-fs-action-surface-inverse text-fs-action-icon-default",
    },
  },
});

const footerStyle = tsv({
  base: [
    "border-t",
    "px-(--slate-spacing-element-large) py-(--slate-spacing-element-large)",
  ],
  variants: {
    theme: {
      light: "border-fs-border-muted",
      dark: "border-(--slate-color-grey-100)/10",
    },
  },
});

const createFormStyle = tsv({
  base: "flex items-end gap-(--slate-spacing-group-medium)",
});

const newCollectionRowStyle = tsv({
  base: [
    "group focusable interactive rounded-fs-component-default flex w-full items-center text-left",
    "gap-(--slate-spacing-group-medium)",
  ],
  variants: {
    // With the placeholder thumbnail the row matches the 40px collection rows
    // (and the small Save button that replaces it).
    withThumbnail: {
      true: "min-h-10",
      false: "min-h-8",
    },
  },
});

const newCollectionPlaceholderStyle = tsv({
  base: [
    "rounded-fs-component-default grid size-10 shrink-0 grid-cols-2 gap-0.5 overflow-hidden",
  ],
  variants: {
    theme: {
      light: "bg-fs-surface-primary",
      dark: "bg-fs-surface-primary-inverse",
    },
  },
});

const newCollectionPlaceholderTileStyle = tsv({
  base: "",
  variants: {
    theme: {
      light: "bg-fs-surface-tertiary",
      dark: "bg-fs-surface-primary/10",
    },
  },
});
